/**
 * Escapes and formats a field for CSV output
 * @param {any} field - Field value to escape
 * @returns {string} - CSV-safe string
 */
const escapeCsvField = (field) => {
  let stringField = String(field ?? ''); // Ensure it's a string, handle null/undefined

  // Remove all occurrences of '#'
  stringField = stringField.replace(/#/g, '');

  // Check if quoting is needed due to special characters
  if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
    // Escape double quotes within the string and wrap in double quotes
    const escapedField = stringField.replace(/"/g, '""');
    return `"${escapedField}"`;
  }
  return stringField;
};

/**
 * Creates a CSV row from an object of field values
 * @param {Object} fieldValues - Object containing field values
 * @returns {string} - Formatted CSV row ending with newline
 */
const createCsvRow = (fieldValues) => {
  return Object.values(fieldValues).join(',') + '\n';
};

/**
 * Converts Entrata lease JSON data to CSV format
 * @param {Object} JsonArray - The lease data from Entrata
 * @param {string} buildingName - Name of the building
 * @param {string} buildingId - ID of the building
 * @param {string} buildingLink - Link to the building
 * @param {string} buildingCode - Building code
 * @returns {string} - Formatted CSV string
 */
export const EntrataLeaseJsonToCSV = (
  JsonArray,
  buildingName,
  buildingId,
  buildingLink,
  buildingCode
) => {
  const JsonFields = [
    'firstName',
    'lastName',
    'fullName',
    'unitNumber',
    'email',
    'phoneNumber',
    'leaseStartDate',
    'leaseEndDate',
    'buildingName',
    'buildingId',
    'buildingLink',
    'buildingCode',
    'Occupant Type',
    'Relationship'
  ];
  let csvStr = `${JsonFields.join(',')}\n`;

  if (!JsonArray?.lease || !Array.isArray(JsonArray.lease)) {
    return csvStr;
  }

  JsonArray.lease.forEach((element, leaseIndex) => {
    if (!element?.customers?.customer || !Array.isArray(element.customers.customer)) {
      return;
    }

    element.customers.customer.forEach((customer, customerIndex) => {
      try {
        // Skip children records
        if (customer?.relationshipName === 'Child') {
          return;
        }

        // Extract customer data
        const data = {
          firstName: escapeCsvField(customer?.firstName ?? ''),
          lastName: escapeCsvField(customer?.lastName ?? ''),
          fullName: escapeCsvField((customer?.firstName ?? '') + ' ' + (customer?.lastName ?? '')),
          unitNumber: escapeCsvField(element?.unitNumberSpace ?? ''),
          email: escapeCsvField(customer?.addresses?.address?.email ?? ''),
          phoneNumber: escapeCsvField(customer?.addresses?.phone?.phoneNumber ?? ''),
          leaseStartDate: escapeCsvField(element?.leaseIntervals?.leaseInterval?.[0]?.startDate ?? ''),
          leaseEndDate: escapeCsvField(element?.leaseIntervals?.leaseInterval?.[0]?.endDate ?? ''),
          buildingName: escapeCsvField(buildingName),
          buildingId: escapeCsvField(buildingId),
          buildingLink: escapeCsvField(buildingLink),
          buildingCode: escapeCsvField(buildingCode),
          occupantType: escapeCsvField(customer?.customerType ?? 'Other'),
          relationshipName: escapeCsvField(customer?.relationshipName ?? 'Other')
        };

        csvStr += createCsvRow(data);
      } catch (error) {
        // Silent error handling - errors expected in some cases
      }
    });
  });
  
  return csvStr;
};

/**
 * Converts Yardi lease JSON data to CSV format
 * @param {Array} residents - Array of resident data from Yardi
 * @param {string} buildingName - Name of the building
 * @param {string} buildingId - ID of the building
 * @param {string} buildingLink - Link to the building
 * @param {string} buildingCode - Building code
 * @returns {string} - Formatted CSV string
 */
export const YardiLeaseJsonToCSV = (
  residents,
  buildingName,
  buildingId,
  buildingLink,
  buildingCode
) => {
  const JsonFields = [
    'firstName',
    'lastName',
    'fullName',
    'unitNumber',
    'email',
    'phoneNumber',
    'occupantType',
    'primaryResident',
    'leaseStartDate',
    'leaseEndDate',
    'buildingName',
    'buildingId',
    'buildingLink',
    'buildingCode',
    'status'
  ];
  let csvStr = `${JsonFields.join(',')}\n`;

  if (!Array.isArray(residents)) {
    return csvStr;
  }

  residents.forEach((customer, index) => {
    try {
      const data = {
        firstName: escapeCsvField(customer.firstName),
        lastName: escapeCsvField(customer.lastName),
        fullName: escapeCsvField((customer.firstName || '') + ' ' + (customer.lastName || '')),
        unitNumber: escapeCsvField(customer.unit),
        email: escapeCsvField(customer.email),
        phoneNumber: escapeCsvField(customer.phone),
        occupantType: escapeCsvField(customer.occupantType),
        primaryResident: escapeCsvField(customer.primaryResident),
        leaseStartDate: escapeCsvField(customer.details?.LeaseFromDate),
        leaseEndDate: escapeCsvField(customer.details?.LeaseToDate),
        buildingName: escapeCsvField(buildingName),
        buildingId: escapeCsvField(buildingId),
        buildingLink: escapeCsvField(buildingLink),
        buildingCode: escapeCsvField(buildingCode),
        status: escapeCsvField(customer.yardiStatus)
      };

      csvStr += createCsvRow(data);
    } catch (error) {
      // Silent error handling - errors expected in some cases
    }
  });
  
  return csvStr;
};

export default EntrataLeaseJsonToCSV;
